// src/pages/admin/analytics/components/ProductProfitabilityTab.jsx
import { useState } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp, Filter, Search, ShieldAlert, CheckCircle, Package } from 'lucide-react';

const ProductProfitabilityTab = ({ data, loading }) => {
  const [filterStatus, setFilterStatus] = useState('ALL'); // 'ALL' | 'LOSS' | 'LOW' | 'HIGH'
  const [searchTerm, setSearchTerm] = useState('');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <span className="font-medium text-lg">Calculating Product Profitability & Margins...</span>
      </div>
    );
  }

  const products = data?.products || [];
  const summary = data?.summary || {};

  // Filter products by search & status
  const filteredProducts = products.filter((item) => {
    const matchesSearch =
      item.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === 'LOSS') return item.status === 'loss_making';
    if (filterStatus === 'LOW') return item.status === 'low_margin';
    if (filterStatus === 'HIGH') return item.status === 'high_profit';
    return true;
  });

  const lossMakingProducts = products.filter((p) => p.status === 'loss_making');

  return (
    <div className="space-y-6">
      {/* Loss Making Products Alert Banner */}
      {lossMakingProducts.length > 0 && (
        <div className="p-5 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-xl shadow-lg border border-red-500 flex items-start space-x-4 animate-in fade-in duration-300">
          <div className="p-3 bg-white/20 rounded-lg">
            <ShieldAlert className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black tracking-wide">
                ACTION REQUIRED: {lossMakingProducts.length} Loss-Making Product(s) Detected
              </h3>
              <span className="px-3 py-1 bg-white text-red-700 font-bold rounded-full text-xs uppercase tracking-wider">
                Menu Optimization Needed
              </span>
            </div>
            <p className="text-sm text-red-100 mt-1">
              These items cost more to produce & prep than they earn in sales revenue. Review recipe costs, increase prices, or eliminate them from the menu to restore profitability.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {lossMakingProducts.map((lp) => (
                <span key={lp.productId} className="px-2.5 py-1 bg-white/20 text-white rounded-md text-xs font-semibold">
                  ⚠️ {lp.productName} (Margin: {lp.marginPct}%)
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Products Analyzed</p>
          <p className="text-2xl font-black text-gray-900 mt-1">{summary.totalProductsSold || products.length}</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Overall Net Margin</p>
          <p className="text-2xl font-black text-emerald-600 mt-1">{summary.overallMargin || 0}%</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Total Product Net Profit</p>
          <p className="text-2xl font-black text-blue-600 mt-1">Rs. {(summary.totalNetProfit || 0).toLocaleString('en-US')}</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-rose-200 bg-rose-50/30 shadow-sm">
          <p className="text-xs font-semibold text-rose-600 uppercase">Loss-Making Items</p>
          <p className="text-2xl font-black text-rose-700 mt-1">{summary.lossMakingCount || lossMakingProducts.length}</p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center space-x-2 w-full md:w-auto overflow-x-auto">
          <span className="text-xs font-bold text-gray-500 uppercase mr-1 flex items-center">
            <Filter className="h-3.5 w-3.5 mr-1" /> Filter:
          </span>
          <button
            onClick={() => setFilterStatus('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'ALL' ? 'bg-gray-900 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Products ({products.length})
          </button>
          <button
            onClick={() => setFilterStatus('LOSS')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'LOSS' ? 'bg-red-600 text-white shadow-sm' : 'bg-red-50 text-red-700 hover:bg-red-100'
            }`}
          >
            Loss-Making ({lossMakingProducts.length})
          </button>
          <button
            onClick={() => setFilterStatus('LOW')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'LOW' ? 'bg-amber-600 text-white shadow-sm' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
          >
            Low Margin (&lt;15%)
          </button>
          <button
            onClick={() => setFilterStatus('HIGH')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'HIGH' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            High Profit (&ge;35%)
          </button>
        </div>

        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search product or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>

      {/* Products Profitability Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
              <tr>
                <th className="p-4">Product / SKU</th>
                <th className="p-4 text-center">Units Sold</th>
                <th className="p-4 text-right">Selling Price</th>
                <th className="p-4 text-right">GRN Cost</th>
                <th className="p-4 text-right">Total Revenue</th>
                <th className="p-4 text-right">COGS</th>
                <th className="p-4 text-right">Wastage Cost</th>
                <th className="p-4 text-right">Net Profit</th>
                <th className="p-4 text-center">Margin %</th>
                <th className="p-4 text-left">Recommendation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-8 text-center text-gray-500">
                    No products match the selected criteria.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p) => {
                  const isLoss = p.status === 'loss_making';
                  const isLow = p.status === 'low_margin';
                  const isHigh = p.status === 'high_profit';

                  return (
                    <tr
                      key={p.productId}
                      className={`hover:bg-gray-50/80 transition-colors ${
                        isLoss ? 'bg-red-50/40' : ''
                      }`}
                    >
                      <td className="p-4">
                        <p className="font-bold text-gray-900">{p.productName}</p>
                        <p className="text-[10px] text-gray-500">SKU: {p.sku || 'N/A'}</p>
                      </td>
                      <td className="p-4 text-center font-bold text-gray-800">{p.unitsSold}</td>
                      <td className="p-4 text-right">Rs. {p.avgSellingPrice.toFixed(2)}</td>
                      <td className="p-4 text-right text-gray-600">Rs. {p.unitCost.toFixed(2)}</td>
                      <td className="p-4 text-right font-bold text-gray-900">Rs. {p.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="p-4 text-right text-slate-700">Rs. {p.totalCOGS.toFixed(2)}</td>
                      <td className="p-4 text-right text-rose-600">
                        {p.wastageCost > 0 ? `Rs. ${p.wastageCost.toFixed(2)}` : '-'}
                      </td>
                      <td className={`p-4 text-right font-black ${p.netProfit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        Rs. {p.netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold ${
                            isLoss
                              ? 'bg-red-100 text-red-800 border border-red-300'
                              : isLow
                              ? 'bg-amber-100 text-amber-800 border border-amber-300'
                              : isHigh
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {p.marginPct}%
                        </span>
                      </td>
                      <td className="p-4 text-xs">
                        <span
                          className={`block p-1.5 rounded-md text-[11px] ${
                            isLoss
                              ? 'bg-red-100 text-red-900 font-semibold'
                              : isLow
                              ? 'bg-amber-50 text-amber-900'
                              : isHigh
                              ? 'bg-emerald-50 text-emerald-900'
                              : 'text-gray-600'
                          }`}
                        >
                          {p.recommendation}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductProfitabilityTab;
